// M5TextAndImage.tsx
import classNames from 'classnames';
import styles from './M5TextAndImage.module.scss';
import { useMemo } from 'react';
import { Container } from 'ui/components/4-habitats/Container';
import { Heading, Picture, LinkButton } from 'ui/components/1-atoms';
import { EditorialRichText } from 'ui/components/2-molecules';
import IconArrowRight from 'ui/icons/icon-arrow-right-web.svg';

export interface M5TextAndImageProps {
	className?: string;
	dictionary?: { [key: string]: string };
	variant?: 'stacked' | 'split' | 'splitTall';
	title?: string;
	subtitle?: string;
	text?: string;
	isAcademyHero?: boolean;
	link1?: { url: string; name?: string; target?: string };
	link2?: { url: string; name?: string; target?: string };
	link3?: { url: string; name?: string; target?: string };
	image?: { url: string; altText?: string; focalPoint?: { left: number; top: number }; extension?: string };
	anchor?: string;
}

export const M5TextAndImage: React.FC<M5TextAndImageProps> = ({
	className,
	dictionary,
	title,
	subtitle,
	text,
	link1,
	link2,
	link3,
	image,
	variant = 'split',
	isAcademyHero = false,
	anchor,
}) => {
	const aspectRatio = useMemo(() => {
		switch (variant) {
			case 'stacked':
				return 16 / 9;
			case 'splitTall':
				return 16 / 6;
			case 'split':
			default:
				return 21 / 9;
		}
	}, [variant]);

	// De-dupe
	const links = [link1, link2, link3].filter((link): link is NonNullable<typeof link> => Boolean(link?.url));

	return (
		<div
			className={classNames(
				styles.M5TextAndImage,
				variant ? styles[`is${variant.charAt(0).toUpperCase() + variant.slice(1)}`] : null,
				{ [styles.M5TextAndImage___academy]: isAcademyHero },
				className,
			)}
			id={anchor}
		>
			<Container width="StandardWeb" className={styles.M5TextAndImage_wrapper}>
				<div className={styles.M5TextAndImage_colRight}>
					{variant === 'stacked' && (
						<div className={styles.M5TextAndImage_headingsContainer}>
							<Heading headingLevel="h1" style="displayLg" className={styles.M5TextAndImage_heading}>
								{title}
							</Heading>
							<p className={styles.M5TextAndImage_subHeading}>{subtitle}</p>
						</div>
					)}
					<Picture url={image?.url} aspectRatio={aspectRatio} className={styles.M30ProductFeaturesCard_imageElement} />
				</div>

				<div className={styles.M5TextAndImage_colLeft}>
					{variant !== 'stacked' && (
						<div className={styles.M5TextAndImage_headingsContainer}>
							<Heading headingLevel="h2" style="displaySm" className={styles.M5TextAndImage_heading}>
								{title}
							</Heading>
							<p className={styles.M5TextAndImage_subHeading}>{subtitle}</p>
						</div>
					)}

					<div className={styles.M5TextAndImage_descriptionContainer}>
						<EditorialRichText variant="website" content={text} className={styles.M5TextAndImage_description} />

						{links.map((link, i) => (
							<LinkButton
								withBackIcon={<IconArrowRight />}
								key={`${link.url}-${i}`}
								style="primaryWeb"
								url={link.url}
								target={link.target}
								className={styles.M5TextAndImage_button}
								title={dictionary?.buttonTitle}
							>
								{link.name}
							</LinkButton>
						))}
					</div>
				</div>
			</Container>
		</div>
	);
};
